import React from 'react'
import Icon from '../Icon/index'
import { makeIcon } from '../OracleCommons/icons'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

const SAGE_PERSONALITY_ID = '2'
const ARCHITECT_PERSONALITY_ID = '1'

export interface SidebarNewConversationButtonsProps {
  onNewChat: (personalityId: string) => void
  /** Currently-active personality id; the matching button reflects the selection */
  selectedPersonalityId?: string
}

/**
 * Two stacked buttons for starting a new conversation from the sidebar:
 *  - Sage:      personality id '2'
 *  - Architect: personality id '1'
 *
 * The button matching `selectedPersonalityId` is the active personality: it gets
 * the `--selected` modifier (dark gradient fill + new-chat icon) and
 * `aria-pressed`. The other renders as a plain text link (no fill / no border).
 */
export const SidebarNewConversationButtons: React.FC<SidebarNewConversationButtonsProps> = ({
  onNewChat,
  selectedPersonalityId,
}) => {
  const sageLabel = useTranslatedText({ keys: ['sidebarNewConversation', 'sage'] })
  const architectLabel = useTranslatedText({ keys: ['sidebarNewConversation', 'architect'] })

  const isSageSelected = selectedPersonalityId === SAGE_PERSONALITY_ID
  const isArchitectSelected = selectedPersonalityId === ARCHITECT_PERSONALITY_ID

  return (
    <div className="sidebar-new-conversation">
      <button
        type="button"
        data-testid="sidebar-new-conversation-sage"
        className={`sidebar-new-conversation__sage${isSageSelected ? ' sidebar-new-conversation__button--selected' : ''}`}
        aria-pressed={isSageSelected}
        onClick={() => onNewChat(SAGE_PERSONALITY_ID)}
      >
        {isSageSelected && (
          <span className="sidebar-new-conversation__icon" aria-hidden="true">
            <Icon color="currentColor" size={24}>
              {makeIcon('new-chat')}
            </Icon>
          </span>
        )}
        <span className="sidebar-new-conversation__label">{sageLabel}</span>
      </button>
      <button
        type="button"
        data-testid="sidebar-new-conversation-architect"
        className={`sidebar-new-conversation__architect${isArchitectSelected ? ' sidebar-new-conversation__button--selected' : ''}`}
        aria-pressed={isArchitectSelected}
        onClick={() => onNewChat(ARCHITECT_PERSONALITY_ID)}
      >
        {isArchitectSelected && (
          <span className="sidebar-new-conversation__icon" aria-hidden="true">
            <Icon color="currentColor" size={24}>
              {makeIcon('new-chat')}
            </Icon>
          </span>
        )}
        <span className="sidebar-new-conversation__label">{architectLabel}</span>
      </button>
    </div>
  )
}
